import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import anime from 'animejs';
import { useLandbotContext } from 'context/landbot.context';
import useLandbot from 'hooks/useLandbot';
import useLandbotVisibility from 'hooks/useLandbotVisibility';
import useLandbotSpeaker from 'hooks/useLandbotSpeaker';
import useMessagePipeline from 'hooks/useMessagePipeline';
import useKeyboardShortcuts from 'hooks/useKeyboardShortcuts';
import useInput from 'hooks/useInput';
import TYPES from 'constants/TYPES';
import EVENTS from 'constants/EVENTS';

import Typed from 'typed.js';

import { landbotHeight } from 'game/definitions';

const LandbotWrapper = styled.div`
  position: absolute;
  width: 100%;
  min-height: ${landbotHeight}px;
  left: 0;
  bottom: 0;
  cursor: pointer;
  pointer-events: none;
  transition: opacity 0.5s;
  opacity: 0;
  backdrop-filter: blur(6px);
  background-color: rgba(0, 0, 0, 0.4);

  @media (min-width: 768px) {
    width: calc(100% - 30px);
    left: 15px;
    bottom: 20px;
  }

  &.is-active {
    opacity: 1;
    pointer-events: initial;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledContinueHelp = styled.div`
  opacity: 0;
`;

const ContinueHelp = () => {
  const elemRef = useRef(null);

  useLayoutEffect(() => {
    anime({
      targets: elemRef.current,
      opacity: [0, 0.4, 0.4, 0],
      easing: 'easeInOutQuad',
      delay: 200,
      duration: 2000,
      loop: true,
    });
  }, []);

  return (
    <StyledContinueHelp ref={elemRef}>
      <span>Click / Press ENTER to continue ›</span>
    </StyledContinueHelp>
  );
};

const ChatWrapper = styled.div`
  flex: 1;
  min-height: 0;
  padding: 10px 20px;

  .Message {
    font-size: 18px;
  }
`;

const InputWrapper = styled.div`
  padding: 10px 20px 20px;
`;

const SpeakerWrapper = styled.div`
  width: ${landbotHeight}px;
`;

const Image = styled.img`
  object-fit: contain;
`;

const Message = ({ rich_text, message, title, setCanContinue }) => {
  const elemRef = useRef(null);
  const content = rich_text || message || title;

  useLayoutEffect(() => {
    if (elemRef && content) {
      setCanContinue(false);
      const type = new Typed(elemRef.current, {
        strings: [content],
        typeSpeed: 1,
        showCursor: false,
        contentType: 'html',
        onComplete: () => setCanContinue(true),
      });
      return () => type.destroy();
    }
  }, [elemRef]);

  return <div className="Message" ref={elemRef} />;
};

const Button = styled.button`
  outline: 0;
  background-color: rgba(84, 86, 162, 0.1);
  color: white !important;
  font-size: 18px;
  border-radius: 8px;
  transition-duration: 0.1s;
  transition-timing-function: ease-in-out;
  min-width: 80px;
  height: auto;

  &:hover {
    background-color: rgba(84, 86, 162, 0.3);
  }

  @media (min-width: 468px) {
    min-width: 100px;
  }
`;

const InputTextUI = styled.input`
  font-size: 18px;
  border-width: 3px;
  height: auto;
  padding: 10px 16px;
`;

const InputText = (props) => {
  const inputRef = useRef(null);

  useLayoutEffect(() => {
    try {
      inputRef.current.focus();
    } catch (e) {
      console.error(e);
    }
  }, []);

  return <InputTextUI ref={inputRef} {...props} />;
};

const InputButtons = (props) => {
  const buttonsRef = useRef(null);

  useLayoutEffect(() => {
    try {
      buttonsRef.current.querySelector('.button').focus();
    } catch (e) {
      console.error(e);
    }
  }, []);

  return (
    <div className="buttons" ref={buttonsRef}>
      {props.buttons.map(({ text, payload }) => (
        <Button
          key={payload}
          className="button"
          onClick={() =>
            props.submit({
              type: 'button',
              message: text,
              payload,
            })
          }
        >
          {text}
        </Button>
      ))}
    </div>
  );
};

const InputUI = styled.div`
  opacity: 0;

  .help {
    opacity: 0.5;
  }
`;

const Input = ({ type, buttons, submit }) => {
  const [disabled, setDisabled] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [submitable, setSubmitable] = useState(false);
  const inputRef = useRef(null);

  const _submit = (data) => {
    if (submitable && data.message) {
      setDisabled(true);
      submit(data);
    }
  };

  useEffect(() => {
    anime({
      targets: inputRef.current,
      opacity: [0, 1],
      delay: 500,
      duration: 600,
      easing: 'easeInOutQuad',
      complete: () => {
        setSubmitable(true);
      },
    });
  }, []);

  useEffect(() => {
    if (disabled) {
      setSubmitable(false);
    }
  }, [disabled]);

  return disabled ? null : (
    <InputUI className="Input" ref={inputRef}>
      {[TYPES.INPUT.TEXT, TYPES.INPUT.BUTTONS].includes(type) && (
        <p className="help mb-1">Answer:</p>
      )}
      {type === TYPES.INPUT.TEXT && (
        <div className="field has-addons">
          <div className="control is-expanded mr-0">
            <InputText
              className="input has-background-black has-color-white"
              type="text"
              placeholder="Type here..."
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  _submit({ message: inputValue });
                }
              }}
              onChange={(e) => setInputValue(e.target.value)}
              value={inputValue}
            />
          </div>
          <div className="control">
            <Button
              className="button has-text-weight-bold is-uppercase"
              onClick={() => _submit({ message: inputValue })}
            >
              Send
            </Button>
          </div>
        </div>
      )}
      {type === TYPES.INPUT.BUTTONS && (
        <InputButtons buttons={buttons} submit={_submit} />
      )}
    </InputUI>
  );
};

Landbot.defaultProps = {
  mode: 'sequential',
  forceVisible: false,
  config: {
    configUrl:
      'https://chats.landbot.io/v3/H-728985-KI54TOCDS445FI2D/index.json',
  },
};

function Landbot(props) {
  const {
    message,
    messages,
    next,
    end,
    canContinue,
    setCanContinue,
  } = useMessagePipeline(props.mode);
  const { landbot } = useLandbotContext();
  const { inputProps } = useInput(message);
  const visible = useLandbotVisibility();
  const speaker = useLandbotSpeaker();

  useKeyboardShortcuts(next, canContinue);

  const submit = (data) => landbot.events.emit(EVENTS.SEND_MESSAGE, data);

  return (
    <div className="is-overlay" style={{ zIndex: 1 }}>
      <LandbotWrapper
        className={classNames(
          'columns is-gapless mb-0 is-unselectable',
          (visible || props.forceVisible) && 'is-active'
        )}
        onClick={canContinue && !('ontouchstart' in window) ? next : null}
        onTouchStart={canContinue && 'ontouchstart' in window ? next : null}
      >
        <SpeakerWrapper className="column is-narrow is-hidden-mobile">
          <div className="px-2 py-2">
            <figure className="image is-square">
              <Image src={speaker} />
            </figure>
          </div>
        </SpeakerWrapper>
        <ContentWrapper className="column">
          <ChatWrapper>
            <Message
              key={message.key}
              next={next}
              canContinue={canContinue}
              setCanContinue={setCanContinue}
              {...message}
            />
          </ChatWrapper>
          <InputWrapper>
            {inputProps.type && (
              <Input key={message.key} submit={submit} {...inputProps} />
            )}
            {props.mode === 'click' &&
              !inputProps.type &&
              !end &&
              !message.isPlaceholder &&
              canContinue && <ContinueHelp key={message.key} />}
          </InputWrapper>
        </ContentWrapper>
      </LandbotWrapper>
    </div>
  );
}

export default function LandbotLoader(props) {
  const { landbot } = useLandbotContext();
  const { isFetching } = useLandbot(props.config);

  return landbot ? <Landbot {...props} /> : null;
}
